import React, { useEffect, useState } from 'react';
import { useSeason } from '@/context/SeasonContext';

interface Particle {
    id: number;
    left: number;
    size: number;
    duration: number;
    delay: number;
    opacity: number;
    swayDuration: number;
}

const SeasonalLayer: React.FC = () => {
    const { season } = useSeason();
    const [particles, setParticles] = useState<Particle[]>([]);

    useEffect(() => {
        if (season === 'none') {
            setParticles([]);
            return;
        }

        console.log(`🌸 Initializing Season: ${season}`);
        const count = window.innerWidth < 768 ? 20 : 50;

        const newParticles: Particle[] = Array.from({ length: count }).map((_, i) => ({
            id: i,
            left: Math.random() * 100,
            size: Math.random() * 6 + 4, // 4-10px base size
            duration: Math.random() * 5 + 5, // 5-10s fall
            delay: Math.random() * 5,
            opacity: Math.random() * 0.5 + 0.3,
            swayDuration: Math.random() * 2 + 2 // 2-4s sway
        }));

        setParticles(newParticles);
    }, [season]);

    if (season === 'none') return null;

    const getParticleContent = () => {
        switch (season) {
            case 'winter': return (
                <div className="rounded-full bg-white shadow-[0_0_5px_rgba(255,255,255,0.8)] w-full h-full" />
            );
            case 'halloween': return (
                <div className="text-orange-500">🎃</div>
            );
            case 'valentine': return (
                <div className="text-red-500">❤️</div>
            );
            case 'stpats': return (
                <div className="text-green-500">☘️</div>
            );
            default: return null;
        }
    };

    return (
        <div className="fixed inset-0 pointer-events-none z-[100000] overflow-hidden" aria-hidden="true">
            {particles.map((p) => (
                <div
                    key={p.id}
                    className="absolute top-[-50px]"
                    style={{
                        left: `${p.left}%`,
                        opacity: p.opacity,
                        fontSize: `${p.size * 2}px`, // Scale emojis
                        width: `${p.size}px`,
                        height: `${p.size}px`,
                        animation: `season-fall ${p.duration}s linear infinite`,
                        animationDelay: `${p.delay}s`
                    }}
                >
                    {/* Inner wrapper for sway to avoid transform conflicts */}
                    <div style={{
                        animation: `season-sway ${p.swayDuration}s ease-in-out infinite alternate`,
                        width: '100%',
                        height: '100%'
                    }}>
                        {getParticleContent()}
                    </div>
                </div>
            ))}

            {/* Inline Styles for isolated animations */}
            <style>{`
                @keyframes season-fall {
                    0% { transform: translateY(-10vh); }
                    100% { transform: translateY(110vh); }
                }
                @keyframes season-sway {
                    0% { transform: translateX(-15px) rotate(-10deg); }
                    100% { transform: translateX(15px) rotate(10deg); }
                }
            `}</style>
        </div>
    );
};

export default SeasonalLayer;
